import { computed, nextTick, watch, type Ref } from 'vue';
import { useRepo } from 'pinia-orm';
import { UserStep } from '../models/UserStep';
import type { Try, UserStepProgress } from 'types/course/step';

const userStepRepo = useRepo(UserStep);

export type ProgressData = {
  result: boolean;
  answer?: string;
  duration: number;
  final?: boolean;
  isHelp?: boolean;
  isTimeout?: boolean;
};

type WithId = { id?: number | null } | null | undefined;

export interface UseProgressRecorderOptions {
  repoUserStep: Readonly<Ref<UserStep | null>>;
  currentParsedProgress: Readonly<Ref<UserStepProgress | null>>;
  /** The step (its id becomes progress.id on the first write). */
  documentStepRepo: Readonly<Ref<WithId>>;
  /** The step's drill (drill.id on the first write). */
  stepDrill: Readonly<Ref<WithId>>;
  /** step_type[0] (its stack.id on the first write). */
  stepType0: Readonly<Ref<{ stack?: WithId } | null | undefined>>;
  orderedStackSource: Readonly<Ref<readonly unknown[]>>;
  currentStackItem: Readonly<Ref<{ id?: number | string } | null | undefined>>;
  updateStrapiProgress: (
    userStepData: Partial<UserStep>,
    opts?: { stampStart?: boolean },
  ) => void;
}

/**
 * Writes answers (and a skipped filter) into the user step's progress, locally
 * first, then through updateStrapiProgress (moved verbatim from useStep).
 */
export function useProgressRecorder({
  repoUserStep,
  currentParsedProgress,
  documentStepRepo,
  stepDrill,
  stepType0,
  orderedStackSource,
  currentStackItem,
  updateStrapiProgress,
}: UseProgressRecorderOptions) {
  /** The row (and the step) the progress is written to are loaded. */
  const canRecord = computed(
    () => !!repoUserStep.value?.id && !!documentStepRepo.value?.id,
  );

  /**
   * Tries answered before the user-step row is in the store (deep link):
   * kept with the question they answered, written once the row appears.
   */
  let pendingTries: Array<{ qId: string; data: ProgressData }> = [];

  /** Writes one try into the ORM progress; returns the new progress. */
  function applyTry(
    qId: string,
    { result, answer, duration, final = true, isHelp, isTimeout }: ProgressData,
  ): UserStepProgress {
    const currentTry: Try = { duration };
    if (result) currentTry['isCorrect'] = true;
    if (answer) currentTry['answer'] = answer;
    if (isHelp) currentTry['isHelp'] = true;
    if (isTimeout) currentTry['isTimeout'] = true;

    // Build a NEW progress object; the parsed ORM progress is never mutated.
    const old = currentParsedProgress.value;
    const oldResults = old?.stack?.results ?? {};
    const entry: NonNullable<UserStepProgress['stack']['results']>[string] = {
      tries: [...(oldResults[qId]?.tries ?? []), currentTry],
    };
    // On repeat tries, set incomplete to false when correct
    if (!final) entry.incomplete = !result;

    // First write wins for id / drill.id / stack.id / stack.length (D-1, D-2).
    const progress: UserStepProgress = {
      ...old,
      id: old?.id ?? documentStepRepo.value?.id,
      drill: old?.drill ?? { id: stepDrill.value?.id },
      stack: {
        ...old?.stack,
        id: old?.stack?.id ?? stepType0.value?.stack?.id,
        length: old?.stack?.length ?? orderedStackSource.value.length,
        results: { ...oldResults, [qId]: entry },
      },
    } as UserStepProgress;

    userStepRepo
      .where('documentId', repoUserStep.value?.documentId)
      .update({ progress });
    return progress;
  }

  /** Writes the buffered tries (in order); returns the last progress. */
  function applyPendingTries(): UserStepProgress | null {
    const tries = pendingTries;
    pendingTries = [];
    let progress: UserStepProgress | null = null;
    for (const { qId, data } of tries) progress = applyTry(qId, data);
    return progress;
  }

  // The row arrived: write the buffered tries and save them (one PUT).
  watch(canRecord, (ready) => {
    if (!ready || !pendingTries.length) return;
    const progress = applyPendingTries();
    if (progress) updateStrapiProgress({ progress });
  });

  function updateProgress(data: ProgressData) {
    if (!currentStackItem.value) {
      console.error('No current stack item');
      return;
    }
    const qId = `q_${currentStackItem.value.id}`;

    // No row yet (deep link): keep the try until the row loads.
    if (!canRecord.value) {
      pendingTries.push({ qId, data });
      return;
    }

    // A try answered before the row loaded goes first.
    applyPendingTries();
    const progress = applyTry(qId, data);

    // Snapshot now; the save queue sends it on the next tick.
    updateStrapiProgress({ progress });
  }

  /**
   * The filter stack has loaded: an empty one is recorded as a skipped step.
   * An already-completed step stays completed: no skip is recorded.
   */
  function recordSkippedFilter(items: readonly unknown[] | null | undefined) {
    if (items?.length === 0 && !repoUserStep.value?.completion_date) {
      // update the progress with skipped filter step
      const progress = Object.assign(
        { isSkippedFilter: true },
        currentParsedProgress.value,
      );

      userStepRepo
        .where('documentId', repoUserStep.value?.documentId)
        .update({ progress });

      // A skip is not a start: no start_date, no step:start (BUG-33).
      void nextTick().then(() =>
        updateStrapiProgress({ progress }, { stampStart: false }),
      );
    }
  }

  return { updateProgress, recordSkippedFilter };
}

export type UseProgressRecorderReturn = ReturnType<typeof useProgressRecorder>;
